import * as xlsx from "xlsx";
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import {
  ImportExcelColumn,
  ImportExcelSubmitResult,
} from "./ImportExcelTypes";

/** Chuẩn hoá tiêu đề cột để so khớp giữa file excel và cấu hình cột */
const normalizeHeader = (value: any) =>
  String(value ?? "")
    .replace(/\(\*\)/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

export const templateHeader = (column: ImportExcelColumn) =>
  column.required ? `${column.header} (*)` : column.header;

/** Các cột có mặt trên file excel mẫu */
export const templateColumns = (columns: ImportExcelColumn[]) =>
  columns.filter((c) => c.inTemplate !== false);

/**
 * Đọc file excel thành danh sách dòng dữ liệu thô, key theo cấu hình cột.
 * Nhận cả tiêu đề tiếng Việt trên file mẫu và tên field (khi import lại file đã export).
 */
export const readExcelToRows = (
  file: Blob,
  columns: ImportExcelColumn[]
): Promise<Record<string, any>[]> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onerror = () => reject(new Error("Không đọc được tệp !"));

    fileReader.onload = (e) => {
      try {
        const data = e.target?.result;
        if (!data) {
          resolve([]);
          return;
        }

        const workbook = xlsx.read(data, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!worksheet) {
          resolve([]);
          return;
        }

        // Đọc theo mảng để tự dò dòng tiêu đề, tránh phụ thuộc vào dòng đầu tiên
        const matrix = xlsx.utils.sheet_to_json<any[]>(worksheet, {
          header: 1,
          defval: null,
          raw: false,
          blankrows: false,
        });

        const cols = templateColumns(columns);
        const headerAlias = new Map<string, string>();
        cols.forEach((c) => {
          headerAlias.set(normalizeHeader(c.header), c.key);
          headerAlias.set(normalizeHeader(c.key), c.key);
        });

        const headerRowIndex = matrix.findIndex((row) =>
          (row ?? []).some((cell) => headerAlias.has(normalizeHeader(cell)))
        );
        if (headerRowIndex < 0) {
          reject(
            new Error(
              "Không tìm thấy dòng tiêu đề khớp với file mẫu, vui lòng tải lại file mẫu !"
            )
          );
          return;
        }

        const headerRow = matrix[headerRowIndex] ?? [];
        const keyByColumnIndex = new Map<number, string>();
        headerRow.forEach((cell, index) => {
          const key = headerAlias.get(normalizeHeader(cell));
          if (key && !Array.from(keyByColumnIndex.values()).includes(key)) {
            keyByColumnIndex.set(index, key);
          }
        });

        const rows: Record<string, any>[] = [];
        for (let i = headerRowIndex + 1; i < matrix.length; i++) {
          const line = matrix[i] ?? [];
          const row: Record<string, any> = {};
          let hasValue = false;

          keyByColumnIndex.forEach((key, columnIndex) => {
            const raw = line[columnIndex];
            const value =
              raw === null || raw === undefined ? "" : String(raw).trim();
            row[key] = value;
            if (value.length > 0) hasValue = true;
          });

          if (hasValue) rows.push(row);
        }

        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };

    fileReader.readAsBinaryString(file);
  });

/** Số dòng được áp dụng dropdown trong file excel mẫu */
const TEMPLATE_DROPDOWN_ROWS = 500;

/** Giới hạn của inline list trong OOXML data validation */
const INLINE_LIST_MAX_LENGTH = 255;

/** Chuyển số thứ tự cột (0-based) sang chữ cái cột excel: 0 -> A, 26 -> AA */
const columnLetter = (index: number) => {
  let result = "";
  let current = index;
  while (current >= 0) {
    result = String.fromCharCode((current % 26) + 65) + result;
    current = Math.floor(current / 26) - 1;
  }
  return result;
};

const escapeXml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * Danh sách giá trị được chọn trực tiếp trong file excel cho một cột.
 * Cột boolean luôn có 2 lựa chọn; cột select chỉ áp dụng khi danh sách đủ ngắn
 * để nhồi vào inline list của OOXML (giới hạn 255 ký tự).
 */
const dropdownValues = (column: ImportExcelColumn): string[] | null => {
  if (column.type === "boolean") {
    return [
      column.booleanLabels?.trueLabel ?? "Đang sử dụng",
      column.booleanLabels?.falseLabel ?? "Dừng sử dụng",
    ];
  }

  if (column.type === "select" && Array.isArray(column.options) && column.options.length > 0) {
    const labels = column.options.map((o) => o.label);
    if (labels.join(",").length <= INLINE_LIST_MAX_LENGTH) return labels;
  }

  return null;
};

/**
 * SheetJS bản community không ghi được thẻ dataValidations nên phải chèn tay:
 * giải nén file xlsx vừa tạo, thêm thẻ vào sheet "Data" rồi nén lại.
 * Lỗi ở bước này không được làm hỏng việc tải file mẫu.
 */
const injectDataValidations = (
  fileData: Uint8Array,
  cols: ImportExcelColumn[]
): Uint8Array => {
  const rules = cols
    .map((column, index) => ({ column, index }))
    .map(({ column, index }) => {
      const values = dropdownValues(column);
      if (!values) return null;

      const letter = columnLetter(index);
      const range = `${letter}2:${letter}${TEMPLATE_DROPDOWN_ROWS}`;
      const list = escapeXml(values.join(","));

      return (
        `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="1"` +
        ` errorTitle="Giá trị không hợp lệ" error="Vui lòng chọn một giá trị trong danh sách."` +
        ` sqref="${range}"><formula1>&quot;${list}&quot;</formula1></dataValidation>`
      );
    })
    .filter((rule): rule is string => rule !== null);

  if (rules.length === 0) return fileData;

  try {
    const unzipped = unzipSync(fileData);
    const sheetPath = "xl/worksheets/sheet1.xml";
    const sheetXml = unzipped[sheetPath];
    if (!sheetXml) return fileData;

    let xml = strFromU8(sheetXml);
    const block = `<dataValidations count="${rules.length}">${rules.join("")}</dataValidations>`;

    // Theo schema, dataValidations phải nằm sau sheetData (và sau mergeCells nếu có)
    const anchor = xml.includes("</mergeCells>") ? "</mergeCells>" : "</sheetData>";
    if (!xml.includes(anchor)) return fileData;

    xml = xml.replace(anchor, `${anchor}${block}`);
    unzipped[sheetPath] = strToU8(xml);

    return zipSync(unzipped);
  } catch (error) {
    console.error("[ImportExcel] Không chèn được dropdown vào file mẫu", error);
    return fileData;
  }
};

/** Tải file excel mẫu theo cấu hình cột */
export const downloadExcelTemplate = (
  columns: ImportExcelColumn[],
  fileName: string,
  sampleRows?: Record<string, any>[]
) => {
  const cols = templateColumns(columns);
  const headers = cols.map(templateHeader);
  const body = (sampleRows ?? []).map((row) =>
    cols.map((c) => {
      const value = row[c.key];
      return value === null || value === undefined ? "" : value;
    })
  );

  const worksheet = xlsx.utils.aoa_to_sheet([headers, ...body]);
  worksheet["!cols"] = cols.map((c) => ({
    wch: Math.max(15, Math.min(45, templateHeader(c).length + 6)),
  }));

  const guide = xlsx.utils.aoa_to_sheet([
    ["Cột", "Bắt buộc", "Giá trị cho phép / Ghi chú"],
    ...cols.map((c) => [
      templateHeader(c),
      c.required ? "Có" : "Không",
      Array.isArray(c.options) && c.options.length > 0
        ? c.options.map((o) => o.label).join(" | ")
        : c.type === "boolean"
        ? `${c.booleanLabels?.trueLabel ?? "Đang sử dụng"} | ${
            c.booleanLabels?.falseLabel ?? "Dừng sử dụng"
          }${c.description ? ` — ${c.description}` : ""}`
        : c.description ?? "",
    ]),
  ]);
  guide["!cols"] = [{ wch: 32 }, { wch: 10 }, { wch: 70 }];

  const workbook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workbook, worksheet, "Data");
  xlsx.utils.book_append_sheet(workbook, guide, "Ghi chu");

  const raw = new Uint8Array(
    xlsx.write(workbook, { type: "array", bookType: "xlsx" })
  );
  const fileData = injectDataValidations(raw, cols);

  const blob = new Blob([fileData], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const EXCEL_TRUE_VALUES = [
  "1", "true", "x", "co", "có", "yes", "y",
  "đang sử dụng", "dang su dung", "sử dụng", "su dung",
];

const EXCEL_FALSE_VALUES = [
  "0", "false", "khong", "không", "no", "n",
  "dừng sử dụng", "dung su dung", "ngừng sử dụng",
];

/** Đọc giá trị boolean từ excel: true/false, 1/0, x, có/không, đang sử dụng/dừng sử dụng */
export const parseExcelBoolean = (
  value: any,
  defaultValue = false
): boolean => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text.length === 0) return defaultValue;
  if (EXCEL_TRUE_VALUES.includes(text)) return true;
  if (EXCEL_FALSE_VALUES.includes(text)) return false;
  return defaultValue;
};

/**
 * Giống parseExcelBoolean nhưng trả về null khi giá trị không nhận dạng được,
 * để validateRows báo lỗi rõ ràng thay vì âm thầm lấy giá trị mặc định.
 * Ô để trống vẫn trả về defaultValue.
 */
export const parseExcelBooleanOrNull = (
  value: any,
  defaultValue = false
): boolean | null => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text.length === 0) return defaultValue;
  if (EXCEL_TRUE_VALUES.includes(text)) return true;
  if (EXCEL_FALSE_VALUES.includes(text)) return false;
  return null;
};

/**
 * Chuyển MessageResponse của api create-list thành kết quả cho ImportExcelShell.
 * Api trả về data = { totalCount, successCount, failCount, errors: [{ rowIndex, code, message }] }
 */
export const toImportSubmitResult = (
  response: any,
  successMessage?: string
): ImportExcelSubmitResult => {
  // repositoryXxx trả về undefined khi lỗi mạng / lỗi 500, thông báo đã được hiển thị sẵn
  if (!response) return { success: false };

  const data = response.data ?? response.Data;
  const rawErrors: any[] = data?.errors ?? data?.Errors ?? [];

  return {
    success: Boolean(response.success ?? response.Success),
    message: response.success
      ? successMessage ?? response.message
      : response.message,
    errors: rawErrors.map((e) => ({
      rowIndex: Number(e.rowIndex ?? e.RowIndex ?? 0),
      code: e.code ?? e.Code ?? null,
      message: e.message ?? e.Message ?? "",
    })),
  };
};
